import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router";
import { Check, ChevronDown, X, Loader2 } from "lucide-react";
import { cn, hasPermission } from "@/lib/utils";
import { PERMISSIONS } from "@/constants/permissions";
import { BackButton } from "@/components/ui/BackButton";
import { SearchInput } from "@/components/ui/SearchInput/SearchInput";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Modal, ModalContent } from "@/components/ui/Modal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import BasicInformation from "./components/BasicInformation";
import BenchmarkCard from "./components/BenchmarkCard";
import BatchSummary from "./components/BatchSummary";
import StandardOperatingProcedure, { StandardOperatingProcedureOthers } from "./components/StandardOperatingProcedure";
import { AssignTaskModal } from "@/features/project-overview/components/TaskAutomationModals";
import { useCreateProjectTask } from "@/hooks/mutations/useProjectTaskMutations";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/queryKeys";
import { useRecipeById, useRecipes, useRecipeVersions } from "@/hooks/useRecipes";
import { useAuthStore } from "@/store/useAuthStore";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { useApplicationLabProjectDetails } from "@/hooks/useMasterProject";

const ADD_SAMPLE_TASKS = [
  {
    title: "HOD Approval",
    responsibility: "HOD Approval",
    module: "application-lab",
    subModule: "lab-records",
  },
];
import { useProjectMembers } from "@/hooks/useProjectMembers";
import { useProjectTasksByProject } from "@/hooks/useProjectTasks";
import { extractTaskBatchSizeAndVersion } from "@/features/profile/UserProfilePage/utils";
import { useCreateSample } from "@/hooks/useSamples";
import { usePackagingTypes } from "@/hooks/usePackagingTypes";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/utils/apiError";
import { buildIngredientsDisplayData, isBeveragePsdRecipe, isBeverageOrBeveragePsdRecipe } from "./data/ingredientsCalculations";
import { parseStoredComments, buildSOPDataFromRecipe } from "./data/sopDataByFormat";

const formatCommentDateTime = (dateVal) => {
  if (!dateVal) return "-";
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return String(dateVal);
  const datePart = d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const timePart = d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${datePart} - ${timePart}`;
};

const formatIngredientCost = (value) => {
  if (value === undefined || value === null || value === "") return "-";
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(2) : String(value);
};

export default function ScaleBatchSamplePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { recipeId, version, projectId: routeProjectId } = useParams();

  const [selectedRecipeId, setSelectedRecipeId] = useState(null);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [selectedVersionNum, setSelectedVersionNum] = useState(null);

  // Navigation state passed when clicking "Sample"
  const stateRecipe = location.state?.recipe;
  const stateVItem = location.state?.vItem;
  const stateVersion = location.state?.version;
  const stateProject = location.state?.project;

  const initialRecipeId =
    selectedRecipeId ||
    recipeId ||
    stateVItem?._id ||
    stateRecipe?._id;

  // Fetch initial recipe if not passed via state or to hydrate missing fields
  const { data: fetchedRecipe, isLoading: isRecipeLoading } = useRecipeById(initialRecipeId, {
    enabled: !!initialRecipeId,
  });

  const targetProjectId =
    routeProjectId ||
    stateProject?._id ||
    fetchedRecipe?.project?._id ||
    (typeof fetchedRecipe?.project === "string" ? fetchedRecipe.project : null) ||
    stateRecipe?.project?._id ||
    (typeof stateRecipe?.project === "string" ? stateRecipe.project : null);

  const { data: projectRecipesData } = useRecipes(
    { project: targetProjectId, limit: 100 },
    { enabled: !!targetProjectId }
  );

  const projectRecipes = useMemo(
    () => projectRecipesData?.data || [],
    [projectRecipesData]
  );

  // Group project recipes into distinct recipe families (one representative per recipe family)
  const uniqueProjectRecipes = useMemo(() => {
    if (!Array.isArray(projectRecipes) || projectRecipes.length === 0) return [];
    const map = new Map();
    for (const r of projectRecipes) {
      const key = r.baseCode || (r.name || r.recipeName || "").trim().toLowerCase() || r._id;
      if (!map.has(key)) {
        map.set(key, r);
      } else {
        const existing = map.get(key);
        if (Number(r.version ?? 0) > Number(existing.version ?? 0)) {
          map.set(key, r);
        }
      }
    }
    return Array.from(map.values());
  }, [projectRecipes]);

  const targetRecipeId =
    selectedRecipeId ||
    initialRecipeId ||
    uniqueProjectRecipes[0]?._id;

  useEffect(() => {
    if (!selectedRecipeId && targetRecipeId) {
      setSelectedRecipeId(targetRecipeId);
    }
  }, [targetRecipeId, selectedRecipeId]);

  const recipe =
    (fetchedRecipe?._id === targetRecipeId ? fetchedRecipe : null) ||
    (stateRecipe?._id === targetRecipeId ? stateRecipe : null) ||
    projectRecipes.find((r) => r._id === targetRecipeId);

  const { data: fetchedProject } = useApplicationLabProjectDetails(targetProjectId, {
    enabled: !!targetProjectId,
  });
  const project = stateProject || fetchedProject?.data?.data || fetchedProject?.data || fetchedProject || recipe?.project;

  const { data: projectMembers = [] } = useProjectMembers(targetProjectId);
  const { permissions = [] } = useUserPermissions();
  const canCreateSample = hasPermission(permissions, PERMISSIONS.SAMPLE.CREATE);

  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const createSampleMutation = useCreateSample();
  const { mutateAsync: createProjectTask } = useCreateProjectTask();
  const { data: packagingTypesData } = usePackagingTypes({ status: "active" });
  const [isCreatingSample, setIsCreatingSample] = useState(false);

  const resolvedPackageTypeId =
    project?.applicationLab?.packagingType?._id ||
    (typeof project?.applicationLab?.packagingType === "string" ? project.applicationLab.packagingType : null) ||
    packagingTypesData?.data?.[0]?.id ||
    null;

  // Fetch recipe versions for targetRecipeId
  const { data: fetchedVersions = [] } = useRecipeVersions(targetRecipeId, {
    enabled: !!targetRecipeId,
  });

  const availableVersions = useMemo(() => {
    let list = [];
    if (Array.isArray(fetchedVersions) && fetchedVersions.length > 0) {
      list = [...fetchedVersions];
    } else if (Array.isArray(recipe?.versions) && recipe.versions.length > 0) {
      list = [...recipe.versions];
    } else if (Array.isArray(fetchedRecipe?.versions) && fetchedRecipe.versions.length > 0) {
      list = [...fetchedRecipe.versions];
    }

    if (list.length === 0) {
      const fallbackVer = (stateVItem?._id === targetRecipeId ? stateVItem : null) || fetchedRecipe || recipe;
      if (fallbackVer) {
        list = [fallbackVer];
      }
    }

    const uniqueMap = new Map();
    list.forEach((item) => {
      const key = item._id ? String(item._id) : `v-${item.version}`;
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, item);
      }
    });

    return Array.from(uniqueMap.values()).sort(
      (a, b) => Number(b.version ?? 0) - Number(a.version ?? 0)
    );
  }, [fetchedVersions, recipe?.versions, fetchedRecipe?.versions, stateVItem, targetRecipeId, fetchedRecipe, recipe]);

  const { data: projectTasksResponse } = useProjectTasksByProject(targetProjectId, {
    enabled: !!targetProjectId,
  });

  const taskFallbackInfo = useMemo(() => {
    const tasks = projectTasksResponse?.data || [];
    const prepTask = tasks.find(
      (t) =>
        (t.title && t.title.toLowerCase().includes("prepare sample")) ||
        (t.module === "application-lab" && t.subModule === "sample-preparation")
    );
    if (!prepTask) return { batchSize: null, recipeVersion: null };
    return extractTaskBatchSizeAndVersion(prepTask);
  }, [projectTasksResponse]);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const queryVersion =
    searchParams.get("version") ||
    searchParams.get("recipeVersion") ||
    searchParams.get("v") ||
    location.state?.version ||
    location.state?.recipeVersion ||
    null;

  const targetVersionParam =
    version !== undefined
      ? version
      : queryVersion !== null
      ? queryVersion
      : taskFallbackInfo.recipeVersion !== null
      ? taskFallbackInfo.recipeVersion
      : null;

  const initialVersionNum = targetVersionParam !== null && targetVersionParam !== undefined
    ? Number(targetVersionParam)
    : (stateVersion ?? stateVItem?.version ?? recipe?.version ?? null);

  const activeVersionObj = useMemo(() => {
    if (selectedVersionId) {
      const found = availableVersions.find((v) => v._id === selectedVersionId);
      if (found) return found;
    }
    if (selectedVersionNum !== null && selectedVersionNum !== undefined) {
      const num = Number(selectedVersionNum);
      const found =
        availableVersions.find((v) => Number(v.version) === num) ||
        availableVersions.find((v) => Number(v.version) + 1 === num);
      if (found) return found;
    }
    if (initialVersionNum !== null && initialVersionNum !== undefined) {
      const num = Number(initialVersionNum);
      // 1. Try exact 0-indexed match (e.g. 2 for stored version 2)
      let found = availableVersions.find((v) => Number(v.version) === num);
      if (found) return found;
      // 2. Try 1-indexed match (e.g. 3 for display "VERSION 03" where version is 2)
      found = availableVersions.find((v) => Number(v.version) + 1 === num);
      if (found) return found;
    }
    return availableVersions[0] || null;
  }, [availableVersions, selectedVersionId, selectedVersionNum, initialVersionNum]);

  const activeVersionId = activeVersionObj?._id || selectedVersionId || targetRecipeId;
  const activeVersionNum = activeVersionObj?.version !== undefined
    ? Number(activeVersionObj.version)
    : (initialVersionNum ?? 0);
  const versionNumStr = String(activeVersionNum + 1).padStart(2, "0");

  const { data: fetchedActiveVersion } = useRecipeById(activeVersionId, {
    enabled: !!activeVersionId && activeVersionId !== targetRecipeId,
  });

  const handleSelectVersion = (v) => {
    setSelectedVersionId(v._id || null);
    setSelectedVersionNum(v.version !== undefined ? Number(v.version) : null);
  };

  const activeVersionDoc = useMemo(() => {
    if (fetchedActiveVersion && fetchedActiveVersion._id === activeVersionId) {
      return fetchedActiveVersion;
    }
    if (fetchedRecipe && fetchedRecipe._id === activeVersionId) {
      return fetchedRecipe;
    }
    if (stateVItem && stateVItem._id === activeVersionId) {
      return stateVItem;
    }
    if (activeVersionObj) {
      return activeVersionObj;
    }
    return fetchedRecipe || (stateRecipe?._id === targetRecipeId ? stateRecipe : null) || recipe || {};
  }, [fetchedActiveVersion, activeVersionId, fetchedRecipe, stateVItem, activeVersionObj, stateRecipe, targetRecipeId, recipe]);

  // Dynamically resolve ingredients from active version
  const versionIngredients = useMemo(() => {
    if (Array.isArray(fetchedActiveVersion?.ingredients) && fetchedActiveVersion.ingredients.length > 0) {
      return fetchedActiveVersion.ingredients;
    }
    if (fetchedRecipe?._id === activeVersionId && Array.isArray(fetchedRecipe?.ingredients) && fetchedRecipe.ingredients.length > 0) {
      return fetchedRecipe.ingredients;
    }
    if (stateVItem?._id === activeVersionId && Array.isArray(stateVItem?.ingredients) && stateVItem.ingredients.length > 0) {
      return stateVItem.ingredients;
    }
    if (Array.isArray(activeVersionDoc?.ingredients) && activeVersionDoc.ingredients.length > 0) {
      return activeVersionDoc.ingredients;
    }
    if (Array.isArray(recipe?.versions)) {
      const found = recipe.versions.find(
        (v) => (v._id && v._id === activeVersionId) || Number(v.version) === activeVersionNum
      );
      if (Array.isArray(found?.ingredients) && found.ingredients.length > 0) {
        return found.ingredients;
      }
    }
    if (recipe?._id === activeVersionId && Array.isArray(recipe?.ingredients) && recipe.ingredients.length > 0) {
      return recipe.ingredients;
    }
    return [];
  }, [fetchedActiveVersion, fetchedRecipe, activeVersionId, stateVItem, activeVersionDoc, recipe, activeVersionNum]);

  const effectiveVersionData = useMemo(() => {
    const base = activeVersionDoc || fetchedActiveVersion || fetchedRecipe || {};
    return {
      ...base,
      recipeCode: activeVersionDoc?.recipeCode || base?.recipeCode || recipe?.recipeCode || "-",
      createdAt: activeVersionDoc?.createdAt || base?.createdAt || recipe?.createdAt,
      outputYield: activeVersionDoc?.outputYield ?? base?.outputYield ?? recipe?.outputYield ?? 0,
      outputServingSize: activeVersionDoc?.outputServingSize ?? base?.outputServingSize ?? recipe?.outputServingSize ?? 0,
      ingredients: versionIngredients,
    };
  }, [activeVersionDoc, fetchedActiveVersion, fetchedRecipe, recipe, versionIngredients]);

  // Calculations for ingredients - completely dynamic
  const computedData = useMemo(() => {
    return buildIngredientsDisplayData(effectiveVersionData, {
      outputYield: effectiveVersionData?.outputYield,
      outputServingSize: effectiveVersionData?.outputServingSize,
    });
  }, [effectiveVersionData]);

  const { ingredients: baseIngredients = [], totals: baseTotals = {} } = computedData;

  // Selected checkboxes for ingredients
  const [selectedIngredientIndexes, setSelectedIngredientIndexes] = useState([]);
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);

  useEffect(() => {
    setSelectedIngredientIndexes([]);
  }, [targetRecipeId, activeVersionId]);

  // Toggle single ingredient selection
  const handleToggleSelect = (index) => {
    setSelectedIngredientIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  // Check if all ingredients are selected
  const allIngredientsSelected =
    baseIngredients.length > 0 && selectedIngredientIndexes.length === baseIngredients.length;


  // Dynamic sample batch size state and preview scaling
  const defaultTotalQuantity = useMemo(() => {
    const sum = baseIngredients.reduce((acc, curr) => acc + (Number(curr.quantity) || 0), 0);
    return sum > 0 ? sum : (Number(baseTotals?.quantity) || 0);
  }, [baseIngredients, baseTotals?.quantity]);

  const batchSizeStorageKey = `sample_batch_size_${targetRecipeId}_${activeVersionId || activeVersionNum}`;
  const storedBatchSize = typeof window !== "undefined" ? sessionStorage.getItem(batchSizeStorageKey) : null;

  const rawBatchSize =
    searchParams.get("batchSize") ||
    searchParams.get("batch_size") ||
    searchParams.get("sampleBatchSize") ||
    searchParams.get("sample_batch_size") ||
    searchParams.get("batch") ||
    location.state?.batchSize ||
    location.state?.batch_size ||
    location.state?.sampleBatchSize ||
    location.state?.sample_batch_size ||
    location.state?.batch ||
    storedBatchSize ||
    null;

  const queryBatchSize = useMemo(() => {
    if (!rawBatchSize) return null;
    const cleaned = String(rawBatchSize).replace(/[^0-9.]/g, "").trim();
    return cleaned && !isNaN(Number(cleaned)) && Number(cleaned) > 0 ? cleaned : null;
  }, [rawBatchSize]);

  const effectiveBatchSize = useMemo(() => {
    if (queryBatchSize) return queryBatchSize;
    if (taskFallbackInfo.batchSize) {
      const cleaned = String(taskFallbackInfo.batchSize).replace(/[^0-9.]/g, "").trim();
      if (cleaned && !isNaN(Number(cleaned)) && Number(cleaned) > 0) {
        return cleaned;
      }
    }
    return null;
  }, [queryBatchSize, taskFallbackInfo.batchSize]);

  const [sampleBatchSize, setSampleBatchSize] = useState(() => {
    if (effectiveBatchSize) {
      return String(effectiveBatchSize);
    }
    return "";
  });

  const hasUserEditedRef = React.useRef(false);

  useEffect(() => {
    hasUserEditedRef.current = false;
    const stored = typeof window !== "undefined" ? sessionStorage.getItem(batchSizeStorageKey) : null;
    if (stored) {
      setSampleBatchSize(String(stored));
      return;
    }
    if (effectiveBatchSize) {
      setSampleBatchSize(String(effectiveBatchSize));
      return;
    }
    if (defaultTotalQuantity > 0) {
      setSampleBatchSize(String(defaultTotalQuantity));
    }
  }, [batchSizeStorageKey, activeVersionId, activeVersionNum]);

  useEffect(() => {
    if (effectiveBatchSize && !hasUserEditedRef.current && !sampleBatchSize) {
      setSampleBatchSize(String(effectiveBatchSize));
      return;
    }
    if (!effectiveBatchSize && defaultTotalQuantity > 0 && (!sampleBatchSize || Number(sampleBatchSize) === 0) && !hasUserEditedRef.current) {
      setSampleBatchSize(String(defaultTotalQuantity));
    }
  }, [defaultTotalQuantity, effectiveBatchSize]);

  const currentBatchSize = Number(sampleBatchSize) || defaultTotalQuantity || 0;
  const scaleFactor = defaultTotalQuantity > 0 && currentBatchSize > 0 ? currentBatchSize / defaultTotalQuantity : 1;

  const displayIngredients = useMemo(() => {
    return baseIngredients.map((item) => {
      const originalQty = Number(item.quantity) || 0;
      const scaledQty = originalQty * scaleFactor;
      const bffRate = Number(item.bffRate ?? item.bffRateAtCreation ?? 0);
      const clientRate = Number(item.clientRate ?? item.clientRateAtCreation ?? 0);
      const scaledBffCost = (bffRate * scaledQty) / 1000;
      const scaledClientCost = (clientRate * scaledQty) / 1000;
      return {
        ...item,
        scaledQuantity: scaledQty,
        displayQuantity: scaledQty % 1 === 0 ? String(scaledQty) : scaledQty.toFixed(2),
        bffRate,
        clientRate,
        bffCost: scaledBffCost,
        clientCost: scaledClientCost,
      };
    });
  }, [baseIngredients, scaleFactor]);

  const totalQuantityDisplay = defaultTotalQuantity > 0 && currentBatchSize > 0
    ? (currentBatchSize % 1 === 0 ? String(currentBatchSize) : currentBatchSize.toFixed(2))
    : (baseTotals?.quantity || "0");

  // Group ingredients by segment (e.g. liquid group, solid group) matching RecipeDetailsTable
  const ingredientGroups = useMemo(() => {
    if (!displayIngredients || displayIngredients.length === 0) return [];

    const groups = [];
    let currentGroup = null;

    displayIngredients.forEach((item, index) => {
      const typeKey = String(item.typeKey || item.type || "").toLowerCase();

      const prevItem = index > 0 ? displayIngredients[index - 1] : null;
      const prevTypeKey = prevItem ? String(prevItem.typeKey || prevItem.type || "").toLowerCase() : "";

      const shouldStartNewGroup =
        !currentGroup ||
        prevItem?.isSeparator ||
        (typeKey !== prevTypeKey &&
          (typeKey === "liquid" ||
            typeKey === "solid" ||
            prevTypeKey === "liquid" ||
            prevTypeKey === "solid"));

      if (shouldStartNewGroup) {
        if (currentGroup) {
          groups.push(currentGroup);
        }
        currentGroup = {
          items: [],
          isSeparator: false,
          solidLiquidValue: item.solidLiquid || "",
        };
      }

      if (item.solidLiquid && !currentGroup.solidLiquidValue) {
        currentGroup.solidLiquidValue = item.solidLiquid;
      }
      if (item.isSeparator) {
        currentGroup.isSeparator = true;
      }

      currentGroup.items.push({ item, index });
    });

    if (currentGroup && currentGroup.items.length > 0) {
      groups.push(currentGroup);
    }

    return groups;
  }, [displayIngredients]);

  // Resolve target version object for procedureOthers & activities
  const targetVersionObj = useMemo(() => {
    if (activeVersionDoc && (activeVersionDoc.procedureOthers || activeVersionDoc.activities || activeVersionDoc._id)) {
      return activeVersionDoc;
    }
    if (stateVItem && stateVItem._id === activeVersionId && (stateVItem.procedureOthers || stateVItem.activities)) {
      return stateVItem;
    }
    if (Array.isArray(recipe?.versions)) {
      const found = recipe.versions.find(
        (v) => (v._id && v._id === activeVersionId) || Number(v.version) === activeVersionNum
      );
      if (found) return found;
    }
    return activeVersionDoc || fetchedRecipe || (stateRecipe?._id === targetRecipeId ? stateRecipe : null) || recipe || {};
  }, [activeVersionDoc, stateVItem, activeVersionId, recipe, activeVersionNum, fetchedRecipe, stateRecipe, targetRecipeId]);

  const { user: currentUser } = useAuthStore();
  const currentUserName = currentUser?.name || currentUser?.fullName || currentUser?.username || "User";

  const dynamicAuthor =
    targetVersionObj?.createdBy?.name ||
    targetVersionObj?.createdBy?.fullName ||
    (typeof targetVersionObj?.createdBy === "string" && targetVersionObj.createdBy.length < 30
      ? targetVersionObj.createdBy
      : null) ||
    recipe?.createdBy?.name ||
    recipe?.createdBy?.fullName ||
    recipe?.independentRecipeRaisedBy ||
    recipe?.raisedBy ||
    currentUserName;

  const [localBatchSummaryOverrides, setLocalBatchSummaryOverrides] = useState({});
  const [localSopOverrides, setLocalSopOverrides] = useState({});

  // Stored comments from recipe/version procedureOthers & activities (synced with StandardOperatingProcedure)
  const storedActivities = useMemo(() => {
    const list = [];
    if (Array.isArray(targetVersionObj?.activities) && targetVersionObj.activities.length > 0) {
      list.push(...targetVersionObj.activities);
    } else if (!targetVersionObj && Array.isArray(recipe?.activities) && recipe.activities.length > 0) {
      list.push(...recipe.activities);
    }

    const procOthers =
      (typeof localSopOverrides.procedureOthers === "string" ? localSopOverrides.procedureOthers : null) ||
      targetVersionObj?.procedureOthers ||
      (!targetVersionObj ? (recipe?.procedureOthers || fetchedRecipe?.procedureOthers || stateRecipe?.procedureOthers) : null);

    if (procOthers && (typeof procOthers === "string" || Array.isArray(procOthers))) {
      const parsed = parseStoredComments(
        procOthers,
        dynamicAuthor,
        targetVersionObj?.updatedAt || targetVersionObj?.createdAt || recipe?.updatedAt || recipe?.createdAt
      );

      parsed.forEach((p) => {
        const textKey = (p.text || p.comment || "").trim();
        const dateKey = p.createdAt ? new Date(p.createdAt).getTime() : 0;
        const exists = list.some((existing) => {
          const eText = (existing.text || existing.comment || "").trim();
          const eDate = existing.createdAt ? new Date(existing.createdAt).getTime() : 0;
          return eText === textKey && (Math.abs(eDate - dateKey) < 1000 || !eDate || !dateKey);
        });
        if (!exists) {
          list.push(p);
        }
      });
    }

    return list;
  }, [targetVersionObj, recipe, fetchedRecipe, stateRecipe, dynamicAuthor, localSopOverrides.procedureOthers]);

  const [localActivities, setLocalActivities] = useState([]);

  useEffect(() => {
    setLocalActivities([]);
  }, [activeVersionId]);

  // Consolidate stored activities and newly posted local comments
  const effectiveActivities = useMemo(() => {
    const list = [...storedActivities];
    localActivities.forEach((local) => {
      const lText = (local.text || local.comment || "").trim();
      const lDate = local.createdAt ? new Date(local.createdAt).getTime() : 0;
      const exists = list.some((existing) => {
        const eText = (existing.text || existing.comment || "").trim();
        const eDate = existing.createdAt ? new Date(existing.createdAt).getTime() : 0;
        return eText === lText && (Math.abs(eDate - lDate) < 1000 || !eDate || !lDate);
      });
      if (!exists) {
        list.push(local);
      }
    });
    return list;
  }, [storedActivities, localActivities]);

  // Comment submission on this page is handled by <StandardOperatingProcedureOthers>,
  // which routes through handleSaveSpecificFields and lands on the sample being
  // created (see the create payload's `sop.procedureOthers`). Nothing on this page
  // may write to the recipe, otherwise preparing a sample would mutate the recipe.

  // Batch Summary Independent Edit States
  const [isBatchSummaryEditing, setIsBatchSummaryEditing] = useState(false);
  const [draftYield, setDraftYield] = useState(
    effectiveVersionData?.yield ?? effectiveVersionData?.outputYield ?? 100
  );
  const [draftServingSize, setDraftServingSize] = useState(
    effectiveVersionData?.servingSize ?? effectiveVersionData?.outputServingSize ?? 100
  );
  const [draftPerPiece, setDraftPerPiece] = useState(
    effectiveVersionData?.perPiece ?? 0
  );
  const [draftPacketQuantity, setDraftPacketQuantity] = useState(
    effectiveVersionData?.packetQuantity ?? 0
  );

  useEffect(() => {
    setDraftYield(effectiveVersionData?.yield ?? effectiveVersionData?.outputYield ?? 100);
    setDraftServingSize(effectiveVersionData?.servingSize ?? effectiveVersionData?.outputServingSize ?? 100);
    setDraftPerPiece(effectiveVersionData?.perPiece ?? 0);
    setDraftPacketQuantity(effectiveVersionData?.packetQuantity ?? 0);
  }, [
    effectiveVersionData?.yield,
    effectiveVersionData?.outputYield,
    effectiveVersionData?.servingSize,
    effectiveVersionData?.outputServingSize,
    effectiveVersionData?.perPiece,
    effectiveVersionData?.packetQuantity,
  ]);


  const handleSaveSpecificFields = async (fieldsToUpdate) => {
    // If saving batch summary fields
    if (
      fieldsToUpdate.yield !== undefined ||
      fieldsToUpdate.outputYield !== undefined ||
      fieldsToUpdate.servingSize !== undefined ||
      fieldsToUpdate.outputServingSize !== undefined ||
      fieldsToUpdate.perPiece !== undefined ||
      fieldsToUpdate.packetQuantity !== undefined
    ) {
      setLocalBatchSummaryOverrides((prev) => ({ ...prev, ...fieldsToUpdate }));
      if (fieldsToUpdate.yield !== undefined || fieldsToUpdate.outputYield !== undefined) {
        setDraftYield(fieldsToUpdate.yield ?? fieldsToUpdate.outputYield);
      }
      if (fieldsToUpdate.servingSize !== undefined || fieldsToUpdate.outputServingSize !== undefined) {
        setDraftServingSize(fieldsToUpdate.servingSize ?? fieldsToUpdate.outputServingSize);
      }
      if (fieldsToUpdate.perPiece !== undefined) setDraftPerPiece(fieldsToUpdate.perPiece);
      if (fieldsToUpdate.packetQuantity !== undefined) setDraftPacketQuantity(fieldsToUpdate.packetQuantity);

      toast.success("Batch summary parameters updated for sample");
    }

    // If saving SOP fields
    if (
      fieldsToUpdate.procedureSOP !== undefined ||
      fieldsToUpdate.procedureParameters !== undefined ||
      fieldsToUpdate.afterBake !== undefined ||
      fieldsToUpdate.ovenTemperatureTunnelBaking !== undefined ||
      fieldsToUpdate.normalBaking !== undefined ||
      fieldsToUpdate.procedureOthers !== undefined
    ) {
      setLocalSopOverrides((prev) => ({ ...prev, ...fieldsToUpdate }));

      toast.success("SOP parameters updated for sample");
    }
  };

  // Modals state
  const [isAddSampleModalOpen, setIsAddSampleModalOpen] = useState(false);
  const [isTaskAssignModalOpen, setIsTaskAssignModalOpen] = useState(false);

  // Dynamic header values
  const recipeName =
    effectiveVersionData?.recipeName ||
    effectiveVersionData?.name ||
    recipe?.recipeName ||
    recipe?.name ||
    "-";
  const recipeCode =
    effectiveVersionData?.recipeCode ||
    recipe?.recipeCode ||
    "-";
  const refCode =
    recipe?.copiedFromRecipe?.recipeCode ||
    project?.projectCode ||
    project?.masterProject?.code ||
    recipe?.project?.projectCode ||
    "-";
  const formatLabel = (
    effectiveVersionData?.recipeFormat ||
    effectiveVersionData?.recipeType ||
    recipe?.recipeFormat ||
    recipe?.recipeType ||
    "bakery"
  ).toLowerCase();
  const isBeveragePsd = isBeveragePsdRecipe(formatLabel);
  const hideRoleColumn = isBeverageOrBeveragePsdRecipe(formatLabel);

  const dynamicBatchSummary = useMemo(() => {
    const ingredientsToUse = (displayIngredients && displayIngredients.length > 0)
      ? displayIngredients.map((item) => ({
          ...item,
          quantity: Number(item.displayQuantity ?? item.quantity ?? 0),
        }))
      : (effectiveVersionData?.ingredients || []);

    const res = buildIngredientsDisplayData(
      {
        ...effectiveVersionData,
        ingredients: ingredientsToUse,
      },
      {
        recipeFormat: formatLabel,
        yield: draftYield,
        outputYield: draftYield,
        servingSize: draftServingSize,
        outputServingSize: draftServingSize,
        perPiece: draftPerPiece,
        packetQuantity: draftPacketQuantity,
      }
    );
    return res.batchSummary;
  }, [
    effectiveVersionData,
    displayIngredients,
    formatLabel,
    draftYield,
    draftServingSize,
    draftPerPiece,
    draftPacketQuantity,
  ]);

  const isRecipeFinal = ["final", "approved", "finalized"].includes(
    String(
      effectiveVersionData?.recipeStatus ||
      effectiveVersionData?.status ||
      activeVersionDoc?.recipeStatus ||
      activeVersionDoc?.status ||
      fetchedRecipe?.recipeStatus ||
      fetchedRecipe?.status ||
      recipe?.recipeStatus ||
      recipe?.status ||
      ""
    ).toLowerCase()
  );
  const creatorName =
    recipe?.createdBy?.name ||
    recipe?.creator?.name ||
    (typeof recipe?.createdBy === "string" ? recipe.createdBy : null) ||
    project?.raisedBy ||
    "-";

  const creationDate = effectiveVersionData?.createdAt
    ? new Date(effectiveVersionData.createdAt).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    : "-";

  // Dynamic Add Sample form fields
  const [addSampleForm, setAddSampleForm] = useState({
    batchSize: "1000",
    perPiece: "0",
    packetQuantity: "0",
    labOutput: "0",
    wastage: "0",
    packagingStatus: "Not Started",
    packageType: "",
  });

  useEffect(() => {
    const batchSizeVal = currentBatchSize || 1000;
    const perPieceVal =
      dynamicBatchSummary?.bffCostCalculation?.perPiece ||
      dynamicBatchSummary?.confectionery?.perPiece ||
      effectiveVersionData?.outputServingSize ||
      0;
    const packetQuantityVal =
      dynamicBatchSummary?.bffCostCalculation?.packetQuantity ||
      dynamicBatchSummary?.confectionery?.packetQuantity ||
      (perPieceVal > 0 ? Math.floor(batchSizeVal / perPieceVal) : 0);
    const labOutputVal =
      dynamicBatchSummary?.bffCostCalculation?.labOutput ||
      dynamicBatchSummary?.confectionery?.labOutput ||
      (effectiveVersionData?.outputYield
        ? ((batchSizeVal * Number(effectiveVersionData.outputYield)) / 100).toFixed(0)
        : batchSizeVal);
    const wastageVal =
      dynamicBatchSummary?.bffCostCalculation?.wastage ||
      dynamicBatchSummary?.confectionery?.wastage ||
      (Number(batchSizeVal) > Number(labOutputVal)
        ? Number(batchSizeVal) - Number(labOutputVal)
        : 0);

    const defaultPkgType =
      resolvedPackageTypeId ||
      packagingTypesData?.data?.[0]?.id ||
      "";

    setAddSampleForm((prev) => ({
      ...prev,
      batchSize: String(batchSizeVal),
      perPiece: String(perPieceVal),
      packetQuantity: String(packetQuantityVal),
      labOutput: String(labOutputVal),
      wastage: String(wastageVal),
      packagingStatus: prev.packagingStatus || "Not Started",
      packageType: prev.packageType || defaultPkgType,
    }));
  }, [currentBatchSize, dynamicBatchSummary, effectiveVersionData, resolvedPackageTypeId, packagingTypesData]);

  // Auto-calculate Lab Output (Packet Quantity x Per Piece) and Wastage (Batch Size - Lab Output)
  useEffect(() => {
    if (!isAddSampleModalOpen) return;
    const pPiece = parseFloat(addSampleForm.perPiece) || 0;
    const pQty = parseFloat(addSampleForm.packetQuantity) || 0;
    const bSize = parseFloat(addSampleForm.batchSize) || 0;

    const computedOutput = pPiece * pQty;
    const computedWastage = Math.max(0, bSize - computedOutput);

    const formattedOutput = String(Number(computedOutput.toFixed(2)));
    const formattedWastage = String(Number(computedWastage.toFixed(2)));

    setAddSampleForm((prev) => {
      if (prev.labOutput === formattedOutput && prev.wastage === formattedWastage) {
        return prev;
      }
      return {
        ...prev,
        labOutput: formattedOutput,
        wastage: formattedWastage,
      };
    });
  }, [isAddSampleModalOpen, addSampleForm.perPiece, addSampleForm.packetQuantity, addSampleForm.batchSize]);

  const handleAddSampleNext = (e) => {
    e.preventDefault();
    if (!canCreateSample) {
      toast.error("You do not have permission to create a sample");
      return;
    }
    if (!targetProjectId) {
      toast.error("Project ID is missing for this recipe sample");
      return;
    }

    setIsAddSampleModalOpen(false);
    setIsTaskAssignModalOpen(true);
  };

  const handleAssignBack = () => {
    setIsTaskAssignModalOpen(false);
    setIsAddSampleModalOpen(true);
  };

  const handleAssignConfirm = async (taskAssignments) => {
    if (!canCreateSample) {
      toast.error("You do not have permission to create a sample");
      return;
    }
    if (!targetProjectId) {
      toast.error("Project ID is missing for this recipe sample");
      return;
    }

    setIsCreatingSample(true);
    try {
      // 1. Create the Sample (creates entry on Sample Preparation & Packaging with Batch Summary & SOP snapshots)
      const samplePayload = {
        project: targetProjectId,
        recipe: activeVersionId || targetRecipeId,
        ...(addSampleForm.packageType || resolvedPackageTypeId
          ? { packageType: addSampleForm.packageType || resolvedPackageTypeId }
          : {}),
        batchSize: parseFloat(addSampleForm.batchSize) || 0,
        output: parseFloat(addSampleForm.labOutput) || 0,
        perPackQuantity: parseFloat(addSampleForm.perPiece) || 0,
        packagingStatus: addSampleForm.packagingStatus || "Not Started",
        batchSummary: {
          outputYield: localBatchSummaryOverrides.yield ?? localBatchSummaryOverrides.outputYield ?? effectiveVersionData?.outputYield ?? null,
          outputServingSize: localBatchSummaryOverrides.servingSize ?? localBatchSummaryOverrides.outputServingSize ?? effectiveVersionData?.outputServingSize ?? null,
          perPiece: parseFloat(addSampleForm.perPiece) || localBatchSummaryOverrides.perPiece || effectiveVersionData?.perPiece || null,
          packetQuantity: parseFloat(addSampleForm.packetQuantity) || localBatchSummaryOverrides.packetQuantity || effectiveVersionData?.packetQuantity || null,
          servingSizeText: effectiveVersionData?.servingSizeText || null,
          diluentName: effectiveVersionData?.diluentName || null,
          diluentVolume: effectiveVersionData?.diluentVolume || null,
          bffCostCalculation: dynamicBatchSummary?.bffCostCalculation || null,
          clientCostCalculation: dynamicBatchSummary?.clientCostCalculation || null,
          output: dynamicBatchSummary?.output || null,
        },
        sop: {
          procedureSOP: localSopOverrides.procedureSOP || effectiveVersionData?.procedureSOP || null,
          procedureParameters: localSopOverrides.procedureParameters || effectiveVersionData?.procedureParameters || [],
          ovenTemperatureTunnelBaking: localSopOverrides.ovenTemperatureTunnelBaking || effectiveVersionData?.ovenTemperatureTunnelBaking || { topTemperature: [], bottomTemperature: [], bakingTime: null, beltSpeed: null },
          normalBaking: localSopOverrides.normalBaking || effectiveVersionData?.normalBaking || { ovenTemperature: [], ovenTime: [], steam: null },
          afterBake: localSopOverrides.afterBake || effectiveVersionData?.afterBake || { weight: null, size: null, aeration: null, aW: null, moisture: null },
          procedureOthers: typeof localSopOverrides.procedureOthers === "string" ? localSopOverrides.procedureOthers : (effectiveActivities || effectiveVersionData?.procedureOthers || null),
          procedureSensoryFeedback: effectiveVersionData?.procedureSensoryFeedback || null,
        },
      };

      await createSampleMutation.mutateAsync(samplePayload);

      // 2. Create the HOD Approval Project Task (creates entry on Application Lab Records)
      const assignment = taskAssignments?.["HOD Approval"];
      const assignedToUsers = Array.isArray(assignment?.assignedTo) && assignment.assignedTo.length > 0
        ? assignment.assignedTo
        : [];

      const fallbackAssignees = assignedToUsers.length > 0
        ? assignedToUsers
        : (user?._id || user?.id ? [user._id || user.id] : []);

      for (const userId of fallbackAssignees) {
        const toIsoOrNull = (dateVal) => {
          if (!dateVal) return null;
          try {
            const d = new Date(dateVal);
            return isNaN(d.getTime()) ? null : d.toISOString();
          } catch {
            return null;
          }
        };

        const taskPayload = {
          title: "HOD Approval",
          description: assignment?.description || "",
          project: targetProjectId,
          assignedTo: userId,
          module: "application-lab",
          subModule: "lab-records",
          startDate: toIsoOrNull(assignment?.startDate) || new Date().toISOString(),
          dueDate: toIsoOrNull(assignment?.dueDate),
          recipeId: activeVersionId || targetRecipeId,
          recipeCode: recipeCode !== "-" ? recipeCode : undefined,
        };

        await createProjectTask(taskPayload);
      }

      // 3. Invalidate relevant queries so Application Lab Records and Samples refresh immediately
      await queryClient.invalidateQueries({ queryKey: queryKeys.samples.all });
      await queryClient.invalidateQueries({ queryKey: queryKeys.projectTasks.all });
      await queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });

      toast.success("Sample created and task assigned successfully");
      setIsTaskAssignModalOpen(false);

      // 4. Redirect to Sample Preparation & Packaging
      navigate(`/application-lab/sample-preparation-and-packaging/${targetProjectId}`);
    } catch (err) {
      console.error("Failed to complete sample and task creation:", err);
      toast.error(getApiErrorMessage(err, "Failed to create sample and task"));
    } finally {
      setIsCreatingSample(false);
    }
  };

  return (
    <section className="flex flex-col bg-transparent page-section-spacing md:px-0 md:flex-1 md:min-h-0">
      <div className="flex-1 min-h-0 w-full overflow-y-auto custom-scrollbar pr-1 space-y-6 lg:space-y-3 xl:space-y-4 2xl:space-y-5 3xl:space-y-6">
        {/* Top Header Row */}
        <div className="flex flex-col gap-3 lg:gap-1.5 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 lg:gap-1.5 xl:gap-2 2xl:gap-2.5 3xl:gap-3 flex-wrap">
              <BackButton onClick={() => navigate(-1)} />

              <h1 className="text-2xl lg:text-[13px] xl:text-[17px] 2xl:text-[19px] 3xl:text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                {recipeName}
              </h1>

              {recipeCode !== "-" && (
                <span className="px-3 lg:px-1.5 xl:px-2 2xl:px-2.5 3xl:px-3 py-0.5 rounded-full border border-[#4B208B] text-[#4B208B] dark:text-purple-300 font-bold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs">
                  {recipeCode}
                </span>
              )}
            </div>

            <div className="hidden sm:flex items-center gap-3 lg:gap-1.5 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
              <SearchInput placeholder="Search..." />
              <ThemeToggle />
            </div>
          </div>

          <div className="flex items-center gap-4 lg:gap-2 xl:gap-2.5 2xl:gap-3 3xl:gap-4 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs">
            <div className="flex items-center gap-1.5 lg:gap-0.5 xl:gap-1 2xl:gap-1 3xl:gap-1.5 font-semibold text-gray-700 dark:text-gray-300">
              <span>Ref:</span>
              <span className="px-2.5 lg:px-1.5 xl:px-2 2xl:px-2 3xl:px-2.5 py-0.5 lg:py-[1px] 2xl:py-0.5 rounded-full border border-[#4B208B] text-[#4B208B] dark:text-purple-300 font-bold">
                {refCode}
              </span>
            </div>

            <div className="flex items-center gap-1.5 lg:gap-0.5 xl:gap-1 2xl:gap-1 3xl:gap-1.5 font-semibold text-gray-700 dark:text-gray-300">
              <span>Format:</span>
              <span className="px-2.5 lg:px-1.5 xl:px-2 2xl:px-2 3xl:px-2.5 py-0.5 lg:py-[1px] 2xl:py-0.5 rounded-full border border-[#4B208B] text-[#4B208B] dark:text-purple-300 font-bold">
                {formatLabel}
              </span>
            </div>

            <div className="text-gray-700 dark:text-gray-300">
              Created By <span className="font-bold text-gray-900 dark:text-white">{creatorName}</span>
            </div>
          </div>
        </div>

        {/* Expandable Basic Information & Benchmark Cards */}
        <div className="flex flex-col bg-white dark:bg-[#0D0B14] border border-[#EEEBF4] dark:border-primary/40 rounded-3xl lg:rounded-xl xl:rounded-2xl 2xl:rounded-2xl 3xl:rounded-3xl p-6 lg:p-3 xl:p-4 2xl:p-5 3xl:p-6 shadow-sm">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-3 xl:gap-4 2xl:gap-5 3xl:gap-6 mb-6 lg:mb-3 xl:mb-4 2xl:mb-5 3xl:mb-6 items-stretch">
            <BasicInformation
              project={project}
              recipe={activeVersionDoc || recipe}
              formatDate={(d) => d || "-"}
              isFinalized={true}
              isExpanded={isAccordionExpanded}
              onToggleExpand={(nextVal) =>
                setIsAccordionExpanded((prev) => (typeof nextVal === "boolean" ? nextVal : !prev))
              }
            />

            <BenchmarkCard
              sopData={buildSOPDataFromRecipe(activeVersionDoc || recipe, formatLabel)}
              recipe={activeVersionDoc || recipe}
              recipeFormat={formatLabel}
              isFinalized={true}
              isExpanded={isAccordionExpanded}
              onToggleExpand={(nextVal) =>
                setIsAccordionExpanded((prev) => (typeof nextVal === "boolean" ? nextVal : !prev))
              }
            />
          </div>

          {/* Scale Batch Container */}
          <div className="flex flex-col pt-2 lg:pt-1 xl:pt-1.5 2xl:pt-1.5 3xl:pt-2">
            <h2 className="text-2xl lg:text-[13px] xl:text-[17px] 2xl:text-[19px] 3xl:text-2xl font-bold text-gray-900 dark:text-white tracking-tight mb-4 lg:mb-2 xl:mb-2.5 2xl:mb-3 3xl:mb-4">
              Scale Batch
            </h2>

            {/* Version Info Card */}
            <div className="p-4 lg:p-2 xl:p-2.5 2xl:p-3 3xl:p-4 rounded-2xl lg:rounded-lg xl:rounded-xl 2xl:rounded-xl 3xl:rounded-2xl bg-[#F7F5FA] dark:bg-primary/10 border border-[#EEEBF4] dark:border-primary/30 flex items-center gap-6 lg:gap-3 xl:gap-4 2xl:gap-5 3xl:gap-6 mb-6 lg:mb-3 xl:mb-4 2xl:mb-5 3xl:mb-6">
              <div className="flex items-center gap-2 lg:gap-1 xl:gap-1.5">
                <span className="px-3.5 lg:px-2 xl:px-2.5 2xl:px-3 3xl:px-3.5 py-1.5 lg:py-0.5 xl:py-1 2xl:py-1 3xl:py-1.5 rounded-xl lg:rounded-md xl:rounded-lg 2xl:rounded-lg 3xl:rounded-xl bg-[#4B208B] text-white font-extrabold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs tracking-wider uppercase inline-flex items-center shadow-sm select-none">
                  VERSION {versionNumStr}
                </span>
                {isRecipeFinal && (
                  <span className="px-3 lg:px-1.5 xl:px-2 2xl:px-2.5 3xl:px-3 py-[3px] lg:py-[1px] xl:py-[2px] 2xl:py-[2.5px] 3xl:py-[3px] rounded-xl lg:rounded-md xl:rounded-lg 2xl:rounded-lg 3xl:rounded-xl bg-white border border-purple-200 dark:bg-primary/30 text-[#4B208B] dark:text-purple-300 font-bold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs">
                    Approved
                  </span>
                )}
              </div>
              <div className="text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px] leading-tight space-y-0.5">
                <p className="text-gray-500 font-medium">
                  Recipe Creation Date:{" "}
                  <span className="font-bold text-gray-800 dark:text-gray-200">
                    {creationDate}
                  </span>
                </p>
                <p className="text-gray-500 font-medium">
                  Recipe Code:{" "}
                  <span className="font-bold text-gray-800 dark:text-gray-200">
                    {recipeCode}
                  </span>
                  {["final", "approved", "finalized"].includes(String(effectiveVersionData?.recipeStatus || activeVersionDoc?.recipeStatus || fetchedRecipe?.recipeStatus || recipe?.recipeStatus || "").toLowerCase()) && (
                    <span className="ml-3 lg:ml-1.5 xl:ml-2 2xl:ml-2.5 3xl:ml-3 text-gray-500 font-medium">
                      In Development Code:{" "}
                      <span className="font-bold text-gray-800 dark:text-gray-200">
                        {effectiveVersionData?.inDevelopmentCode ||
                          activeVersionDoc?.inDevelopmentCode ||
                          fetchedRecipe?.inDevelopmentCode ||
                          recipe?.inDevelopmentCode ||
                          effectiveVersionData?.referenceCode ||
                          activeVersionDoc?.referenceCode ||
                          fetchedRecipe?.referenceCode ||
                          "N/A"}
                      </span>
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Ingredients Table */}
            <h3 className="text-base lg:text-[9.5px] xl:text-xs 2xl:text-sm 3xl:text-base font-bold text-gray-900 dark:text-white mb-3 lg:mb-1.5 xl:mb-2 2xl:mb-2.5 3xl:mb-3">
              Ingredients Table
            </h3>

            <div className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden touch-pan-x border border-[#EEEBF4] dark:border-primary/40 rounded-2xl lg:rounded-lg xl:rounded-xl 2xl:rounded-xl 3xl:rounded-2xl bg-white dark:bg-[#0D0B14]">
              <div className="min-w-[1050px] lg:min-w-[620px] xl:min-w-[800px] 2xl:min-w-[920px] 3xl:min-w-[1050px]">
                {/* Table Column Headers */}
                <div className="flex items-center h-11 lg:h-6 xl:h-7.5 2xl:h-8.5 3xl:h-11 border-b border-[#EEEBF4] dark:border-primary/40 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-bold text-gray-900 dark:text-white bg-white dark:bg-[#0D0B14]">
                  <div className="w-14 lg:w-7 xl:w-9 2xl:w-11 3xl:w-14 h-full border-r border-[#EEEBF4] dark:border-primary/30" />
                  <div className="w-14 lg:w-7 xl:w-9 2xl:w-11 3xl:w-14 text-center border-r border-[#EEEBF4] dark:border-primary/30">SL</div>
                  {!hideRoleColumn && (
                    <div className="w-20 lg:w-10 xl:w-13 2xl:w-16 3xl:w-20 text-center border-r border-[#EEEBF4] dark:border-primary/30">Role</div>
                  )}
                  <div className="flex-1 pl-4 lg:pl-2 xl:pl-2.5 2xl:pl-3 3xl:pl-4 text-left border-r border-[#EEEBF4] dark:border-primary/30">Ingredients</div>
                  {isBeveragePsd ? (
                    <>
                      <div className="w-28 lg:w-14 xl:w-18 2xl:w-22 3xl:w-28 text-center border-r border-[#EEEBF4] dark:border-primary/30 px-1 whitespace-nowrap">Per Sachet (g/pcs)</div>
                      <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 text-center border-r border-[#EEEBF4] dark:border-primary/30 px-1 whitespace-nowrap">Qty (g)</div>
                      <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 text-center border-r border-[#EEEBF4] dark:border-primary/30 px-1 whitespace-nowrap">Cmp (%)</div>
                      <div className="w-28 lg:w-14 xl:w-18 2xl:w-22 3xl:w-28 text-center px-1 whitespace-nowrap">Final Dosing (%)</div>
                    </>
                  ) : (
                    <>
                      <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 text-center border-r border-[#EEEBF4] dark:border-primary/30 px-1 whitespace-nowrap">Qty (g)</div>
                      <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 text-center border-r border-[#EEEBF4] dark:border-primary/30 px-1 whitespace-nowrap">Cmp (%)</div>
                    </>
                  )}
                  <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 text-center border-l border-[#EEEBF4] dark:border-primary/30 px-1 whitespace-nowrap">BFF Rate (৳)</div>
                  <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 text-center border-l border-[#EEEBF4] dark:border-primary/30 px-1 whitespace-nowrap">Client Rate (৳)</div>
                  <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 text-center border-l border-[#EEEBF4] dark:border-primary/30 px-1 whitespace-nowrap">BFF Cost (৳)</div>
                  <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 text-center border-l border-[#EEEBF4] dark:border-primary/30 px-1 whitespace-nowrap">Client Cost (৳)</div>
                  {!isBeveragePsd && (
                    <div className="w-28 lg:w-14 xl:w-18 2xl:w-22 3xl:w-28 text-center border-l border-[#EEEBF4] dark:border-primary/30 px-1 whitespace-nowrap">S/L (%)</div>
                  )}
                </div>

                {/* Table Body Rows - Grouped with group separators and merged S/L (%) matching IngredientTable */}
                <div className="flex flex-col">
                  {displayIngredients.length > 0 ? (
                    ingredientGroups.map((group, gIdx) => (
                      <div
                        key={gIdx}
                        className={cn(
                          "flex",
                          group.isSeparator
                            ? "border-b-2 border-[#4B208B]"
                            : "border-b border-[#EEEBF4] dark:border-primary/30"
                        )}
                      >
                        {/* Left columns: Checkbox, SL, Role, Ingredients, ... */}
                        <div className={cn(
                          "flex-1 flex flex-col divide-y divide-[#EEEBF4] dark:divide-primary/30",
                          !isBeveragePsd && "border-r border-[#EEEBF4] dark:border-primary/30"
                        )}>
                          {group.items.map(({ item, index }) => {
                            const isChecked = selectedIngredientIndexes.includes(index);
                            return (
                              <div
                                key={item._id || item.id || index}
                                className={cn(
                                  "flex items-center h-12 lg:h-6.5 xl:h-8 2xl:h-9.5 3xl:h-12 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-medium text-gray-800 dark:text-gray-200 hover:bg-[#F7F5FA] dark:hover:bg-primary/10 transition-colors",
                                  isChecked && "bg-purple-50/30 dark:bg-primary/5"
                                )}
                              >
                                {/* Checkbox */}
                                <div className="w-14 lg:w-7 xl:w-9 2xl:w-11 3xl:w-14 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30">
                                  <button
                                    type="button"
                                    onClick={() => handleToggleSelect(index)}
                                    className={cn(
                                      "w-4 lg:w-2.5 xl:w-3 2xl:w-3.5 3xl:w-4 h-4 lg:h-2.5 xl:h-3 2xl:h-3.5 3xl:h-4 rounded-md lg:rounded-[3px] flex items-center justify-center cursor-pointer transition-all",
                                      isChecked
                                        ? "bg-[#4B208B] text-white"
                                        : "border-2 border-[#A294BD] dark:border-gray-600 bg-white dark:bg-[#121019] hover:border-[#4B208B]"
                                    )}
                                  >
                                    {isChecked && <Check className="w-3 lg:w-1.5 xl:w-2 2xl:w-2.5 3xl:w-3 h-3 lg:h-1.5 xl:h-2 2xl:h-2.5 3xl:h-3 stroke-[3]" />}
                                  </button>
                                </div>

                                {/* SL */}
                                <div className="w-14 lg:w-7 xl:w-9 2xl:w-11 3xl:w-14 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30">
                                  <span className="w-6 lg:w-3 xl:w-4 2xl:w-5 3xl:w-6 h-6 lg:h-3 xl:h-4 2xl:h-5 3xl:h-6 rounded-full inline-flex items-center justify-center bg-[#EFEAF9] dark:bg-primary/25 text-[#4B208B] dark:text-purple-300 font-bold text-xs lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-xs shrink-0">
                                    {index + 1}
                                  </span>
                                </div>

                                {/* Role */}
                                {!hideRoleColumn && (
                                  <div className="w-20 lg:w-10 xl:w-13 2xl:w-16 3xl:w-20 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 text-gray-600 dark:text-gray-400 text-center truncate px-1 font-medium">
                                    {item.role || "-"}
                                  </div>
                                )}

                                {/* Ingredients */}
                                <div className="flex-1 h-full flex items-center pl-4 lg:pl-2 xl:pl-2.5 2xl:pl-3 3xl:pl-4 border-r border-[#EEEBF4] dark:border-primary/30 font-bold text-gray-900 dark:text-white truncate">
                                  {item.name || item.ingredientName || item.sourceName || "-"}
                                </div>

                                {isBeveragePsd ? (
                                  <>
                                    {/* Per Sachet */}
                                    <div className="w-28 lg:w-14 xl:w-18 2xl:w-22 3xl:w-28 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 font-medium px-1">
                                      <span className="truncate">{item.perSachet ?? "-"}</span>
                                    </div>

                                    {/* Quantity */}
                                    <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 font-medium px-1">
                                      <span className="truncate">{item.displayQuantity ?? item.quantity ?? "-"}</span>
                                    </div>

                                    {/* Composition */}
                                    <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 font-medium px-1">
                                      <span className="truncate">{item.composition ?? "-"}</span>
                                    </div>

                                    {/* Final Dosing */}
                                    <div className="w-28 lg:w-14 xl:w-18 2xl:w-22 3xl:w-28 h-full flex items-center justify-center font-medium px-1">
                                      <span className="truncate">{item.finalDosing ?? "-"}</span>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    {/* Quantity */}
                                    <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 font-medium px-1">
                                      <span className="truncate">{item.displayQuantity ?? item.quantity ?? "-"}</span>
                                    </div>

                                    {/* Composition */}
                                    <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 h-full flex items-center justify-center font-medium px-1">
                                      <span className="truncate">{item.composition ?? "-"}</span>
                                    </div>
                                  </>
                                )}
                                <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 h-full flex items-center justify-center border-l border-[#EEEBF4] dark:border-primary/30 font-medium px-1">
                                  <span className="truncate">{formatIngredientCost(item.bffRate)}</span>
                                </div>
                                <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 h-full flex items-center justify-center border-l border-[#EEEBF4] dark:border-primary/30 font-medium px-1">
                                  <span className="truncate">{formatIngredientCost(item.clientRate)}</span>
                                </div>
                                <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 h-full flex items-center justify-center border-l border-[#EEEBF4] dark:border-primary/30 font-medium px-1">
                                  <span className="truncate">{formatIngredientCost(item.bffCost)}</span>
                                </div>
                                <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 h-full flex items-center justify-center border-l border-[#EEEBF4] dark:border-primary/30 font-medium px-1">
                                  <span className="truncate">{formatIngredientCost(item.clientCost)}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Right column: S/L (%) spanning the entire group with NO inner dividers */}
                        {!isBeveragePsd && (
                          <div className="w-28 lg:w-14 xl:w-18 2xl:w-22 3xl:w-28 flex items-center justify-center text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-semibold text-gray-900 dark:text-white bg-white dark:bg-[#0D0B14]">
                            <span>{group.solidLiquidValue || ""}</span>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="h-24 lg:h-12 xl:h-16 2xl:h-20 3xl:h-24 flex items-center justify-center text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs text-gray-400 dark:text-gray-500">
                      {isRecipeLoading ? "Loading ingredients..." : "No ingredients available for this recipe."}
                    </div>
                  )}
                </div>

                {/* Dynamic Total Row */}
                <div className="flex items-center h-12 lg:h-6.5 xl:h-8 2xl:h-9.5 3xl:h-12 font-bold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs text-[#4B208B] dark:text-purple-300 border-t-2 border-[#4B208B]/40 bg-[#FCFBFD] dark:bg-[#121019]">
                  <div className="w-14 lg:w-7 xl:w-9 2xl:w-11 3xl:w-14 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30">-</div>
                  {!hideRoleColumn && (
                    <div className="w-20 lg:w-10 xl:w-13 2xl:w-16 3xl:w-20 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30">-</div>
                  )}
                  <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 font-bold">
                    Total
                  </div>
                  {isBeveragePsd ? (
                    <>
                      <div className="w-28 lg:w-14 xl:w-18 2xl:w-22 3xl:w-28 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 font-bold px-1">
                        <span className="truncate">{baseTotals?.perSachet ?? "-"}</span>
                      </div>
                      <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 font-bold px-1">
                        <span className="truncate">{totalQuantityDisplay}</span>
                      </div>
                      <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 font-bold px-1">
                        <span className="truncate">{baseTotals?.composition ?? "-"}</span>
                      </div>
                      <div className="w-28 lg:w-14 xl:w-18 2xl:w-22 3xl:w-28 h-full flex items-center justify-center font-bold px-1">
                        <span className="truncate">{baseTotals?.finalDosing ?? "-"}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 font-bold px-1">
                        <span className="truncate">{totalQuantityDisplay}</span>
                      </div>
                      <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 font-bold px-1">
                        <span className="truncate">{baseTotals?.composition ?? "-"}</span>
                      </div>
                    </>
                  )}
                  <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 h-full flex items-center justify-center border-l border-[#EEEBF4] dark:border-primary/30 font-bold px-1">-</div>
                  <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 h-full flex items-center justify-center border-l border-[#EEEBF4] dark:border-primary/30 font-bold px-1">-</div>
                  <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 h-full flex items-center justify-center border-l border-[#EEEBF4] dark:border-primary/30 font-bold px-1">
                    <span className="truncate">{formatIngredientCost(Number(baseTotals?.bffCost || 0) * scaleFactor)}</span>
                  </div>
                  <div className="w-24 lg:w-12 xl:w-16 2xl:w-20 3xl:w-24 h-full flex items-center justify-center border-l border-[#EEEBF4] dark:border-primary/30 font-bold px-1">
                    <span className="truncate">{formatIngredientCost(Number(baseTotals?.clientCost || 0) * scaleFactor)}</span>
                  </div>
                  {!isBeveragePsd && (
                    <div className="w-28 lg:w-14 xl:w-18 2xl:w-22 3xl:w-28 h-full flex items-center justify-center border-l border-[#EEEBF4] dark:border-primary/30 font-bold px-1">
                      <span className="truncate">{baseTotals?.solidLiquid ?? "-"}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sample Batch Size Section */}
            <div className="mt-8 lg:mt-4 xl:mt-5 2xl:mt-6 3xl:mt-8">
              <h3 className="text-base lg:text-[9.5px] xl:text-xs 2xl:text-sm 3xl:text-base font-bold text-gray-900 dark:text-white mb-2 lg:mb-1 xl:mb-1.5 2xl:mb-1.5 3xl:mb-2">
                Sample Batch Size
              </h3>
              <div className="w-full max-w-[320px] sm:max-w-[360px] lg:max-w-[200px] xl:max-w-[250px] 2xl:max-w-[300px] 3xl:max-w-[360px] h-11 lg:h-6 xl:h-7.5 2xl:h-8.5 3xl:h-11 px-4 lg:px-2 xl:px-2.5 2xl:px-3 3xl:px-4 rounded-2xl lg:rounded-lg xl:rounded-xl 2xl:rounded-xl 3xl:rounded-2xl border-2 border-[#8B5CF6] focus-within:border-[#4B208B] flex items-center justify-between bg-white dark:bg-[#121019] shadow-xs transition-colors">
                <input
                  type="number"
                  value={sampleBatchSize}
                  onChange={(e) => {
                    hasUserEditedRef.current = true;
                    const val = e.target.value;
                    setSampleBatchSize(val);
                    try {
                      if (val) {
                        sessionStorage.setItem(batchSizeStorageKey, val);
                      } else {
                        sessionStorage.removeItem(batchSizeStorageKey);
                      }
                    } catch (err) {
                      console.warn("Failed to store sampleBatchSize:", err);
                    }
                  }}
                  placeholder="0"
                  className="w-full bg-transparent border-none outline-none font-bold text-sm lg:text-[7px] xl:text-[9px] 2xl:text-[11px] 3xl:text-sm text-gray-900 dark:text-white"
                />
                <span className="text-sm lg:text-[7px] xl:text-[9px] 2xl:text-[11px] 3xl:text-sm font-semibold text-gray-500 ml-2 lg:ml-1">g</span>
              </div>
              <p className="text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs text-gray-400 dark:text-gray-500 mt-1.5 lg:mt-0.5">
                Adjust the sample batch size to preview updated quantities.
              </p>
            </div>

            {/* Batch Summary & Standard Operating Procedure (SOP) Side by Side */}
            <div className="mt-8 lg:mt-4 xl:mt-5 2xl:mt-6 3xl:mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-3 xl:gap-4 2xl:gap-5 3xl:gap-6 items-start">
              {/* Batch Summary Section */}
              <div className="border border-[#EEEBF4] dark:border-primary/40 rounded-2xl lg:rounded-lg xl:rounded-xl 2xl:rounded-xl 3xl:rounded-2xl overflow-hidden shadow-xs bg-white dark:bg-[#0D0B14]">
                <div className="p-4 lg:p-2 xl:p-2.5 2xl:p-3 3xl:p-4 bg-[#FAF8FD] dark:bg-primary/10 border-b border-[#EEEBF4] dark:border-primary/30">
                  <h3 className="text-base lg:text-[9.5px] xl:text-xs 2xl:text-sm 3xl:text-base font-bold text-gray-900 dark:text-white">
                    Batch Summary
                  </h3>
                </div>
                <BatchSummary
                  vItem={activeVersionDoc || recipe}
                  data={recipe}
                  isFirstVersion={true}
                  vBatchSummary={dynamicBatchSummary}
                  draftYield={draftYield}
                  setDraftYield={setDraftYield}
                  draftServingSize={draftServingSize}
                  setDraftServingSize={setDraftServingSize}
                  draftPerPiece={draftPerPiece}
                  setDraftPerPiece={setDraftPerPiece}
                  draftPacketQuantity={draftPacketQuantity}
                  setDraftPacketQuantity={setDraftPacketQuantity}
                  isBatchSummaryEditing={isBatchSummaryEditing}
                  setIsBatchSummaryEditing={setIsBatchSummaryEditing}
                  onSaveSpecificFields={handleSaveSpecificFields}
                  isConfectionary={formatLabel.includes("confection")}
                  recipeFormat={formatLabel}
                  isBeveragePsd={isBeveragePsd}
                  vIsFinalized={false}
                  isSamplePage={true}
                  showClientCost={!isRecipeFinal}
                  isBffCostEditable={!isRecipeFinal}
                  isFromFinalRecipe={isRecipeFinal}
                />
              </div>

              {/* Standard Operating Procedure (SOP) Section */}
              <div className="border border-[#EEEBF4] dark:border-primary/40 rounded-2xl lg:rounded-lg xl:rounded-xl 2xl:rounded-xl 3xl:rounded-2xl overflow-hidden shadow-xs bg-white dark:bg-[#0D0B14]">
                <div className="p-4 lg:p-2 xl:p-2.5 2xl:p-3 3xl:p-4 bg-[#FAF8FD] dark:bg-primary/10 border-b border-[#EEEBF4] dark:border-primary/30">
                  <h3 className="text-base lg:text-[9.5px] xl:text-xs 2xl:text-sm 3xl:text-base font-bold text-gray-900 dark:text-white">
                    Standard Operating Procedure (SOP)
                  </h3>
                </div>
                <StandardOperatingProcedure
                  vItem={activeVersionDoc || recipe}
                  data={recipe}
                  normalizedVItem={effectiveVersionData}
                  recipeFormat={formatLabel}
                  isFirstVersion={true}
                  onSaveSpecificFields={handleSaveSpecificFields}
                  isConfectionary={formatLabel.includes("confection")}
                  formatDate={formatCommentDateTime}
                  vIsFinalized={false}
                  hideOthers={true}
                  isSamplePage={true}
                  isFromFinalRecipe={isRecipeFinal}
                />
              </div>
            </div>

            {/* Standard Operating Procedure (SOP) - Others Section on Bottom */}
            <div className="mt-8 lg:mt-4 xl:mt-5 2xl:mt-6 3xl:mt-8">
              <StandardOperatingProcedureOthers
                vItem={activeVersionDoc || recipe}
                data={recipe}
                normalizedVItem={effectiveVersionData}
                formatDate={formatCommentDateTime}
                onSaveSpecificFields={handleSaveSpecificFields}
                vIsFinalized={false}
                isSamplePage={true}
                isFromFinalRecipe={isRecipeFinal}
              />
            </div>

            {/* Bottom Actions Footer */}
            <div className="flex items-center justify-between pt-8 lg:pt-4 xl:pt-5 2xl:pt-6 3xl:pt-8 mt-6 lg:mt-3 xl:mt-4 2xl:mt-5 3xl:mt-6 border-t border-[#EEEBF4] dark:border-primary/40">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-8 lg:px-4 xl:px-5 2xl:px-6 3xl:px-8 py-2.5 lg:py-1.5 xl:py-2 2xl:py-2 3xl:py-2.5 rounded-full border border-[#4B208B] text-[#4B208B] dark:text-purple-300 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-bold hover:bg-[#F7F5FA] dark:hover:bg-primary/10 transition-all cursor-pointer"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={!canCreateSample || !allIngredientsSelected}
                onClick={() => setIsAddSampleModalOpen(true)}
                title={
                  !canCreateSample
                    ? "You do not have permission to create a sample"
                    : !allIngredientsSelected
                    ? "Please select all ingredients checkboxes to proceed"
                    : "Proceed to Add Sample"
                }
                className="px-8 lg:px-4 xl:px-5 2xl:px-6 3xl:px-8 py-2.5 lg:py-1.5 xl:py-2 2xl:py-2 3xl:py-2.5 rounded-full bg-[#4B208B] hover:bg-[#3E1B77] disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-bold transition-all shadow-sm cursor-pointer"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add Sample Modal */}
      <Modal open={isAddSampleModalOpen} onOpenChange={setIsAddSampleModalOpen}>
        <ModalContent className="max-w-[440px] lg:max-w-[340px] xl:max-w-[380px] 2xl:max-w-[410px] 3xl:max-w-[440px] p-6 lg:p-3.5 xl:p-4.5 2xl:p-5 3xl:p-6 rounded-3xl lg:rounded-xl xl:rounded-2xl 2xl:rounded-2xl 3xl:rounded-3xl bg-white dark:bg-[#121019] border border-gray-200 dark:border-primary/40 shadow-2xl">
          <div className="flex items-center justify-between mb-5 lg:mb-2.5 xl:mb-3.5 2xl:mb-4 3xl:mb-5">
            <h2 className="text-xl lg:text-sm xl:text-base 2xl:text-lg 3xl:text-xl font-bold text-gray-900 dark:text-white">
              Add Sample
            </h2>
            <button
              type="button"
              onClick={() => setIsAddSampleModalOpen(false)}
              className="w-8 lg:w-5 xl:w-6 2xl:w-7 3xl:w-8 h-8 lg:h-5 xl:h-6 2xl:h-7 3xl:h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 transition-colors cursor-pointer"
            >
              <X className="w-4 lg:w-2.5 xl:w-3 2xl:w-3.5 3xl:w-4 h-4 lg:h-2.5 xl:h-3 2xl:h-3.5 3xl:h-4" />
            </button>
          </div>

          <form onSubmit={handleAddSampleNext} className="space-y-4 lg:space-y-2 xl:space-y-2.5 2xl:space-y-3 3xl:space-y-4">
            {/* Application Recipe */}
            <div>
              <label className="block text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 lg:mb-0.5">
                Application Recipe
              </label>
              <div className="w-full px-4 lg:px-2 xl:px-2.5 2xl:px-3 3xl:px-4 py-3 lg:py-1.5 xl:py-2 2xl:py-2.5 3xl:py-3 rounded-xl lg:rounded-md xl:rounded-lg 2xl:rounded-lg 3xl:rounded-xl bg-[#F0EDF5] dark:bg-[#1D1829] text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <span>{recipeName}</span>
                {recipeCode !== "-" && (
                  <span className="text-gray-400 font-medium">{recipeCode}</span>
                )}
              </div>
            </div>

            {/* Sample creation fields are shared by in-development and finalized recipes. */}
            {/* Batch Size (Editable) */}
                <div>
                  <label className="block text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 lg:mb-0.5">
                    Batch Size (g) <span className="text-[#4B208B] dark:text-purple-300 font-normal">(Editable)</span>
                  </label>
                  <div className="w-full px-4 lg:px-2 xl:px-2.5 2xl:px-3 3xl:px-4 py-3 lg:py-1.5 xl:py-2 2xl:py-2.5 3xl:py-3 rounded-xl lg:rounded-md xl:rounded-lg 2xl:rounded-lg 3xl:rounded-xl bg-[#F0EDF5] dark:bg-[#1D1829] flex items-center justify-between border-2 border-primary/20 focus-within:border-primary">
                    <input
                      type="text"
                      value={addSampleForm.batchSize}
                      onChange={(e) => setAddSampleForm({ ...addSampleForm, batchSize: e.target.value })}
                      className="w-full bg-transparent outline-none font-bold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs text-gray-900 dark:text-white"
                    />
                    <span className="text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-medium text-gray-500 ml-2">g</span>
                  </div>
                </div>

                {/* Packaging Status */}
                <div>
                  <label className="block text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 lg:mb-0.5">
                    Packaging Status
                  </label>
                  <div className="w-full px-4 lg:px-2 xl:px-2.5 2xl:px-3 3xl:px-4 py-3 lg:py-1.5 xl:py-2 2xl:py-2.5 3xl:py-3 rounded-xl lg:rounded-md xl:rounded-lg 2xl:rounded-lg 3xl:rounded-xl bg-[#F0EDF5] dark:bg-[#1D1829] flex items-center justify-between">
                    <select
                      value={addSampleForm.packagingStatus}
                      onChange={(e) => setAddSampleForm({ ...addSampleForm, packagingStatus: e.target.value })}
                      className="w-full bg-transparent outline-none font-bold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs text-gray-900 dark:text-white cursor-pointer"
                    >
                      <option value="Not Started" className="bg-white dark:bg-[#1D1829] text-gray-900 dark:text-white">Not Started</option>
                      <option value="In Progress" className="bg-white dark:bg-[#1D1829] text-gray-900 dark:text-white">In Progress</option>
                      <option value="Completed" className="bg-white dark:bg-[#1D1829] text-gray-900 dark:text-white">Completed</option>
                      <option value="Paused" className="bg-white dark:bg-[#1D1829] text-gray-900 dark:text-white">Paused</option>
                      <option value="Cancelled" className="bg-white dark:bg-[#1D1829] text-gray-900 dark:text-white">Cancelled</option>
                    </select>
                  </div>
                </div>

                {/* Packaging Type */}
                <div>
                  <label className="block text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 lg:mb-0.5">
                    Packaging Type
                  </label>
                  <div className="w-full px-4 lg:px-2 xl:px-2.5 2xl:px-3 3xl:px-4 py-3 lg:py-1.5 xl:py-2 2xl:py-2.5 3xl:py-3 rounded-xl lg:rounded-md xl:rounded-lg 2xl:rounded-lg 3xl:rounded-xl bg-[#F0EDF5] dark:bg-[#1D1829] flex items-center justify-between">
                    <select
                      value={addSampleForm.packageType}
                      onChange={(e) => setAddSampleForm({ ...addSampleForm, packageType: e.target.value })}
                      className="w-full bg-transparent outline-none font-bold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs text-gray-900 dark:text-white cursor-pointer"
                    >
                      {packagingTypesData?.data?.length > 0 ? (
                        packagingTypesData.data.map((pt) => (
                          <option key={pt.id} value={pt.id} className="bg-white dark:bg-[#1D1829] text-gray-900 dark:text-white">
                            {pt.name}
                          </option>
                        ))
                      ) : (
                        <option value="" className="bg-white dark:bg-[#1D1829] text-gray-900 dark:text-white">
                          Select Packaging Type...
                        </option>
                      )}
                    </select>
                  </div>
                </div>

                {/* Per Piece (Editable) */}
                <div>
                  <label className="block text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 lg:mb-0.5">
                    Per Piece (g) <span className="text-[#4B208B] dark:text-purple-300 font-normal">(Editable)</span>
                  </label>
                  <div className="w-full px-4 lg:px-2 xl:px-2.5 2xl:px-3 3xl:px-4 py-3 lg:py-1.5 xl:py-2 2xl:py-2.5 3xl:py-3 rounded-xl lg:rounded-md xl:rounded-lg 2xl:rounded-lg 3xl:rounded-xl bg-[#F0EDF5] dark:bg-[#1D1829] flex items-center justify-between border-2 border-primary/20 focus-within:border-primary">
                    <input
                      type="text"
                      value={addSampleForm.perPiece}
                      onChange={(e) => setAddSampleForm({ ...addSampleForm, perPiece: e.target.value })}
                      className="w-full bg-transparent outline-none font-bold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs text-gray-900 dark:text-white"
                    />
                    <span className="text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-medium text-gray-500 ml-2">g</span>
                  </div>
                </div>

                {/* Packet Quantity (Editable) */}
                <div>
                  <label className="block text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 lg:mb-0.5">
                    Packet Quantity (pcs) <span className="text-[#4B208B] dark:text-purple-300 font-normal">(Editable)</span>
                  </label>
                  <div className="w-full px-4 lg:px-2 xl:px-2.5 2xl:px-3 3xl:px-4 py-3 lg:py-1.5 xl:py-2 2xl:py-2.5 3xl:py-3 rounded-xl lg:rounded-md xl:rounded-lg 2xl:rounded-lg 3xl:rounded-xl bg-[#F0EDF5] dark:bg-[#1D1829] flex items-center justify-between border-2 border-primary/20 focus-within:border-primary">
                    <input
                      type="text"
                      value={addSampleForm.packetQuantity}
                      onChange={(e) => setAddSampleForm({ ...addSampleForm, packetQuantity: e.target.value })}
                      className="w-full bg-transparent outline-none font-bold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs text-gray-900 dark:text-white"
                    />
                    <span className="text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-medium text-gray-500 ml-2">pcs</span>
                  </div>
                </div>

                {/* Lab Output */}
                <div>
                  <label className="block text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 lg:mb-0.5">
                    Lab Output (g)
                  </label>
                  <div className="w-full px-4 lg:px-2 xl:px-2.5 2xl:px-3 3xl:px-4 py-3 lg:py-1.5 xl:py-2 2xl:py-2.5 3xl:py-3 rounded-xl lg:rounded-md xl:rounded-lg 2xl:rounded-lg 3xl:rounded-xl bg-[#F0EDF5] dark:bg-[#1D1829] flex items-center justify-between">
                    <input
                      type="text"
                      value={addSampleForm.labOutput}
                      readOnly
                      className="w-full bg-transparent outline-none font-bold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs text-gray-900 dark:text-white cursor-default"
                    />
                    <span className="text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-medium text-gray-500 ml-2">g</span>
                  </div>
                </div>

                {/* Wastage */}
                <div>
                  <label className="block text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 lg:mb-0.5">
                    Wastage
                  </label>
                  <div className="w-full px-4 lg:px-2 xl:px-2.5 2xl:px-3 3xl:px-4 py-3 lg:py-1.5 xl:py-2 2xl:py-2.5 3xl:py-3 rounded-xl lg:rounded-md xl:rounded-lg 2xl:rounded-lg 3xl:rounded-xl bg-[#F0EDF5] dark:bg-[#1D1829] flex items-center justify-between">
                    <input
                      type="text"
                      value={addSampleForm.wastage}
                      readOnly
                      className="w-full bg-transparent outline-none font-bold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs text-gray-900 dark:text-white cursor-default"
                    />
                    <span className="text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-medium text-gray-500 ml-2">g</span>
                  </div>
                </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3 lg:gap-1.5 xl:gap-2 2xl:gap-2.5 3xl:gap-3 pt-3 lg:pt-1.5">
              <button
                type="button"
                onClick={() => setIsAddSampleModalOpen(false)}
                disabled={isCreatingSample}
                className="flex-1 py-3 lg:py-1.5 xl:py-2 2xl:py-2.5 3xl:py-3 rounded-xl lg:rounded-md xl:rounded-lg 2xl:rounded-lg 3xl:rounded-xl border border-[#4B208B] text-[#4B208B] dark:text-purple-300 font-bold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs hover:bg-[#F7F5FA] dark:hover:bg-primary/10 transition-all cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isCreatingSample}
                className="flex-1 py-3 lg:py-1.5 xl:py-2 2xl:py-2.5 3xl:py-3 rounded-xl lg:rounded-md xl:rounded-lg 2xl:rounded-lg 3xl:rounded-xl bg-[#4B208B] hover:bg-[#3E1B77] text-white font-bold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs transition-all shadow-sm cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isCreatingSample ? (
                  <>
                    <Loader2 className="w-3.5 lg:w-2 xl:w-2.5 2xl:w-3 3xl:w-3.5 h-3.5 lg:h-2 xl:h-2.5 2xl:h-3 3xl:h-3.5 animate-spin" />
                    Adding...
                  </>
                ) : (
                  "Next"
                )}
              </button>
            </div>
          </form>
        </ModalContent>
      </Modal>

      {/* Assign Task Modal (HOD Approval) */}
      <AssignTaskModal
        open={isTaskAssignModalOpen}
        onOpenChange={setIsTaskAssignModalOpen}
        tasks={ADD_SAMPLE_TASKS}
        projectMembers={projectMembers}
        onBack={handleAssignBack}
        onConfirm={handleAssignConfirm}
      />
    </section>
  );
}
